import { StyleSheet } from 'react-native';

import { colors } from '../../colors';
import { SpotStatus } from './RoomLayoutMap.types';

/** Spot cell styles matching SpotLegend exactly: same bg/border as legend squares. */
export const spotCellColors = {
  /** Booked / Unavailable – outline-light in legend; label matches border (low emphasis) */
  booked: {
    backgroundColor: colors.legendCardBg,
    borderColor: colors.legendBookedBorder,
    textColor: colors.legendBookedBorder
  },
  /** Available – outline-dark in legend */
  available: {
    backgroundColor: colors.legendCardBg,
    borderColor: colors.legendAvailableBorder,
    textColor: colors.legendText
  },
  /** Not bookable – slate fill, white label (matches SpotLegend non-bookable swatch) */
  notBookable: {
    backgroundColor: colors.legendNotBookableFill,
    borderColor: colors.legendNotBookableBorder,
    textColor: colors.legendNotBookableLabel
  },
  selected: {
    backgroundColor: colors.legendSelectedBg,
    borderColor: colors.legendSelectedBorder,
    textColor: colors.legendSelectedText
  },
  assigned: {
    backgroundColor: colors.legendAssignedBg,
    borderColor: colors.legendAssignedBorder,
    textColor: colors.legendAssignedText
  },
  activeSelected: {
    backgroundColor: colors.legendCardBg,
    borderColor: colors.legendSelectedBorder,
    textColor: colors.legendSelectedText
  }
} as const;

export enum SpotHighlightKind {
  None = 'none',
  Assigned = 'assigned',
  Active = 'active',
  LegacySelected = 'legacy-selected'
}

type SpotCellVisual =
  | 'assigned'
  | 'activeSelected'
  | 'legacySelected'
  | 'available'
  | 'booked'
  | 'notBookable';

function resolveSpotCellVisual(status: SpotStatus, highlight: SpotHighlightKind): SpotCellVisual {
  switch (highlight) {
    case SpotHighlightKind.Assigned:
      return 'assigned';
    case SpotHighlightKind.Active:
      return 'activeSelected';
    case SpotHighlightKind.LegacySelected:
      return 'legacySelected';
    case SpotHighlightKind.None:
      break;
  }
  switch (status) {
    case SpotStatus.BOOKABLE:
      return 'available';
    case SpotStatus.BLOCKED:
      return 'booked';
    case SpotStatus.NOT_BOOKABLE:
    default:
      return 'notBookable';
  }
}

/** Figma: spot ~56×56 – round numbers for radius/border/padding, ratios used when scaling */
export const SPOT_BASE_SIZE = 56;
export const SPOT_BORDER_RADIUS_RATIO = 11 / SPOT_BASE_SIZE;
export const SPOT_BORDER_WIDTH = 2;
/**
 * Spot-label design tokens at {@link SPOT_BASE_SIZE}px (padding + type size).
 * Smaller cells scale via {@link getSpotLabelLayout}.
 */
export const SPOT_LABEL_PADDING = 4;
export const SPOT_LABEL_FONT_SIZE = 13;
export const GRID_GAP = 8;
export const GRID_H_PADDING = 16;
export const GRID_PADDING_TOP = 8;

/** Given container width and column count, spot size so grid fits and is centered. Spot is always square (width = height). */
export function getSpotSizeForWidth(containerWidth: number, columns: number): number {
  const contentWidth = Math.max(0, containerWidth - GRID_H_PADDING * 2);
  const size = Math.floor((contentWidth - (columns - 1) * GRID_GAP) / columns);
  return Math.max(1, size);
}

const SPOT_LABEL_LINE_HEIGHT_FACTOR = 1.2;

/**
 * Padding and font size scaled from Figma tokens at {@link SPOT_BASE_SIZE}, then clamped so the
 * label fits inside the cell (non-scrollable grids can use spots ~20–30px wide).
 */
export function getSpotLabelLayout(spotSize: number): { padding: number; fontSize: number } {
  const inner = spotSize - 2 * SPOT_BORDER_WIDTH;
  let padding = Math.round((SPOT_LABEL_PADDING / SPOT_BASE_SIZE) * spotSize);
  padding = Math.max(0, Math.min(SPOT_LABEL_PADDING, padding));

  let fontSize = Math.round((SPOT_LABEL_FONT_SIZE / SPOT_BASE_SIZE) * spotSize);
  fontSize = Math.min(SPOT_LABEL_FONT_SIZE, Math.max(1, fontSize));

  const availForText = Math.max(0, inner - 2 * padding);
  const maxFontByHeight = Math.floor(availForText / SPOT_LABEL_LINE_HEIGHT_FACTOR);
  fontSize = Math.min(fontSize, maxFontByHeight);

  return { padding, fontSize: Math.max(1, fontSize) };
}

export function getSpotHighlightKind(
  spotKey: string,
  selectedSpotKey: string | null,
  assignedSpotKey: string | null | undefined
): SpotHighlightKind {
  const hasAssigned = Boolean(assignedSpotKey && spotKey === assignedSpotKey);
  const hasSelected = Boolean(selectedSpotKey && spotKey === selectedSpotKey);
  if (hasAssigned) {
    return SpotHighlightKind.Assigned;
  }
  if (hasSelected) {
    if (assignedSpotKey && selectedSpotKey !== assignedSpotKey) {
      return SpotHighlightKind.Active;
    }
    return SpotHighlightKind.LegacySelected;
  }
  return SpotHighlightKind.None;
}

export function getSpotCellStyle(
  status: SpotStatus,
  highlight: SpotHighlightKind,
  spotSize: number
) {
  const { padding } = getSpotLabelLayout(spotSize);
  const sizeStyle = {
    width: spotSize,
    height: spotSize,
    borderRadius: spotSize * SPOT_BORDER_RADIUS_RATIO,
    padding
  };
  const base = [styles.spotCell, sizeStyle];
  switch (resolveSpotCellVisual(status, highlight)) {
    case 'assigned':
      return [...base, styles.spotCellAssigned];
    case 'activeSelected':
      return [...base, styles.spotCellActiveSelected];
    case 'legacySelected':
      return [...base, styles.spotCellSelected];
    case 'available':
      return [...base, styles.spotCellAvailable];
    case 'booked':
      return [...base, styles.spotCellBooked];
    case 'notBookable':
    default:
      return [...base, styles.spotCellNotBookable];
  }
}

export function getSpotCellEmptyStyle(spotSize: number) {
  return [
    styles.spotCell,
    styles.spotCellEmpty,
    {
      width: spotSize,
      height: spotSize,
      borderRadius: spotSize * SPOT_BORDER_RADIUS_RATIO,
      pointerEvents: 'none' as const
    }
  ];
}

export function getSpotLabelStyle(
  status: SpotStatus,
  highlight: SpotHighlightKind,
  spotSize: number
) {
  const { fontSize } = getSpotLabelLayout(spotSize);
  const base = [styles.spotLabel, { fontSize }];
  switch (resolveSpotCellVisual(status, highlight)) {
    case 'assigned':
      return [...base, styles.spotLabelAssigned];
    case 'activeSelected':
    case 'legacySelected':
      return [...base, styles.spotLabelSelected];
    case 'available':
      return [...base, styles.spotLabelAvailable];
    case 'booked':
      return [...base, styles.spotLabelBooked];
    case 'notBookable':
    default:
      return [...base, styles.spotLabelNotBookable, styles.spotLabelNotBookableTypography];
  }
}

export const styles = StyleSheet.create({
  gridContent: {
    alignSelf: 'center',
    flexDirection: 'column',
    gap: GRID_GAP,
    paddingBottom: 0,
    paddingHorizontal: GRID_H_PADDING,
    paddingTop: GRID_PADDING_TOP
  },
  gridWrapper: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%'
  },
  /** Used when scrollable: width/minHeight set inline from content dimensions */
  gridWrapperScrollable: {
    flexGrow: 0,
    flexShrink: 0
  },
  row: {
    flexDirection: 'row',
    gap: GRID_GAP
  },
  spotCell: {
    alignItems: 'center',
    borderWidth: SPOT_BORDER_WIDTH,
    justifyContent: 'center'
  },
  spotCellActiveSelected: {
    backgroundColor: spotCellColors.activeSelected.backgroundColor,
    borderColor: spotCellColors.activeSelected.borderColor
  },
  spotCellAssigned: {
    backgroundColor: spotCellColors.assigned.backgroundColor,
    borderColor: spotCellColors.assigned.borderColor
  },
  spotCellAvailable: {
    backgroundColor: spotCellColors.available.backgroundColor,
    borderColor: spotCellColors.available.borderColor
  },
  spotCellBooked: {
    backgroundColor: spotCellColors.booked.backgroundColor,
    borderColor: spotCellColors.booked.borderColor
  },
  spotCellEmpty: {
    backgroundColor: colors.transparent,
    borderColor: colors.transparent,
    borderWidth: 0
  },
  spotCellNotBookable: {
    backgroundColor: spotCellColors.notBookable.backgroundColor,
    borderColor: spotCellColors.notBookable.borderColor
  },
  /** NOT_BOOKABLE cell with bundled icon or spot-type prefix: stack above spot label */
  spotCellNotBookableWithIcon: {
    alignItems: 'center',
    flexDirection: 'column',
    gap: 2,
    justifyContent: 'center'
  },
  spotCellSelected: {
    backgroundColor: spotCellColors.selected.backgroundColor,
    borderColor: spotCellColors.selected.borderColor
  },
  spotLabel: {
    fontWeight: '700'
  },
  spotLabelAssigned: {
    color: spotCellColors.assigned.textColor
  },
  spotLabelAvailable: {
    color: spotCellColors.available.textColor
  },
  spotLabelBooked: {
    color: spotCellColors.booked.textColor
  },
  spotLabelNotBookable: {
    color: spotCellColors.notBookable.textColor
  },
  /** Figma: non-bookable spot label (Mulish Bold); app must register Mulish font */
  spotLabelNotBookableTypography: {
    fontFamily: 'Mulish',
    fontSize: 9,
    fontWeight: 'bold',
    letterSpacing: 0,
    lineHeight: 17.2,
    textAlign: 'center'
  },
  spotLabelSelected: {
    color: spotCellColors.selected.textColor
  },
  /** Custom non-bookable spot-type prefix (e.g. MB) when no system icon is used */
  spotTypePrefixLabel: {
    color: spotCellColors.notBookable.textColor,
    fontFamily: 'Mulish',
    fontSize: 11,
    fontWeight: 'bold',
    letterSpacing: 0,
    lineHeight: 13,
    textAlign: 'center'
  }
});
